import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Mail, Plus, LogOut, Clock, CheckCircle, XCircle, Search, RefreshCw, Zap, Shield } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

function DashboardPage() {
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMode, setCreateMode] = useState('random'); // 'random' ou 'custom'
  const [aliasType, setAliasType] = useState('temporary'); // 'temporary' ou 'permanent'
  const [customPrefix, setCustomPrefix] = useState('');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchAliases();
    const interval = setInterval(fetchAliases, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAliases = async () => {
    try {
      const response = await axiosInstance.get('/aliases');
      setAliases(response.data);
    } catch (error) {
      console.error('Error fetching aliases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshEmails = async () => {
    setRefreshing(true);
    try {
      await axiosInstance.post('/emails/fetch');
      // Attendre 2 secondes puis recharger les alias pour voir les nouveaux emails
      setTimeout(() => {
        fetchAliases();
        setRefreshing(false);
      }, 2000);
    } catch (error) {
      console.error('Error refreshing emails:', error);
      setRefreshing(false);
    }
  };

  const handleCreateAlias = async (e) => {
    e.preventDefault();
    setCreateError('');

    if (createMode === 'custom' && (!customPrefix || customPrefix.trim() === '')) {
      setCreateError('Veuillez entrer un préfixe pour le mode personnalisé');
      return;
    }

    if (createMode === 'custom' && !/^[a-z0-9._-]+$/.test(customPrefix)) {
      setCreateError('Format invalide. Utilisez uniquement des lettres, chiffres, points, tirets');
      return;
    }

    setCreating(true);

    try {
      await axiosInstance.post('/aliases', {
        mode: createMode,
        local_part: createMode === 'custom' ? customPrefix : '',
        alias_type: aliasType
      });
      setCustomPrefix('');
      setShowCreateModal(false);
      fetchAliases();
    } catch (error) {
      setCreateError(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const filteredAliases = aliases.filter(alias =>
    alias.alias_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const permanentAliases = filteredAliases.filter(a => a.alias_type === 'permanent');
  const temporaryAliases = filteredAliases.filter(a => a.alias_type === 'temporary');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Email Alias</h1>
                <p className="text-xs text-slate-500">demo-essihault.com</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleRefreshEmails}
                disabled={refreshing}
                className="btn btn-secondary flex items-center space-x-2"
                data-testid="refresh-emails-btn"
                title="Actualiser les emails maintenant"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Actualiser</span>
              </button>
              <button
                onClick={handleLogout}
                className="btn btn-secondary flex items-center space-x-2"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher un alias..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
              data-testid="search-alias-input"
            />
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary flex items-center justify-center space-x-2"
            data-testid="create-alias-btn"
          >
            <Plus className="w-4 h-4" />
            <span>Nouvel alias</span>
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Total</p>
                <p className="text-2xl font-bold text-slate-800">{aliases.length}</p>
              </div>
              <Mail className="w-8 h-8 text-slate-400" />
            </div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Permanents</p>
                <p className="text-2xl font-bold text-indigo-600">{permanentAliases.length}</p>
              </div>
              <Shield className="w-8 h-8 text-indigo-400" />
            </div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Temporaires</p>
                <p className="text-2xl font-bold text-amber-600">{temporaryAliases.length}</p>
              </div>
              <Zap className="w-8 h-8 text-amber-400" />
            </div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Expirés</p>
                <p className="text-2xl font-bold text-red-600">
                  {aliases.filter(a => a.status === 'expired').length}
                </p>
              </div>
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
          </div>
        </div>

        {/* Aliases List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Chargement...</p>
          </div>
        ) : filteredAliases.length === 0 ? (
          <div className="text-center py-12">
            <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-2">Aucun alias trouvé</p>
            <p className="text-sm text-slate-500">Créez votre premier alias pour commencer</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="aliases-list">
            {filteredAliases.map((alias) => (
              <div
                key={alias.id}
                onClick={() => navigate(`/alias/${alias.id}`)}
                className="card hover:shadow-lg transition-all cursor-pointer"
                data-testid={`alias-item-${alias.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-slate-800">
                        {alias.alias_email}
                      </h3>
                      <span
                        className={`badge ${
                          alias.alias_type === 'permanent' 
                            ? 'bg-indigo-100 text-indigo-700'
                            : alias.status === 'active' ? 'badge-active' : 'badge-expired'
                        }`}
                      >
                        {alias.alias_type === 'permanent' ? (
                          <span className="flex items-center space-x-1">
                            <Shield className="w-3 h-3" />
                            <span>Permanent</span>
                          </span>
                        ) : (
                          <span className="flex items-center space-x-1">
                            <Zap className="w-3 h-3" />
                            <span>{alias.status === 'active' ? 'Temporaire' : 'Expiré'}</span>
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>
                          Créé {formatDistanceToNow(new Date(alias.created_at), {
                            addSuffix: true,
                            locale: fr,
                          })}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Mail className="w-4 h-4" />
                        <span>{alias.email_count} email(s)</span>
                      </div>
                      {alias.last_received_at && (
                        <div className="flex items-center space-x-1">
                          <CheckCircle className="w-4 h-4" />
                          <span>
                            Dernier: {formatDistanceToNow(new Date(alias.last_received_at), {
                              addSuffix: true,
                              locale: fr,
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="card max-w-md w-full animate-fade-in">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">Créer un nouvel alias</h2>
            <form onSubmit={handleCreateAlias} className="space-y-4">
              {/* Mode de génération */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Mode de génération
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setCreateMode('random')}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      createMode === 'random'
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="mode-random-btn"
                  >
                    <Zap className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                    <p className="text-sm font-medium">Aléatoire</p>
                    <p className="text-xs text-slate-500">x7k2p9@...</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreateMode('custom')}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      createMode === 'custom'
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="mode-custom-btn"
                  >
                    <Mail className="w-5 h-5 mx-auto mb-1 text-indigo-500" />
                    <p className="text-sm font-medium">Personnalisé</p>
                    <p className="text-xs text-slate-500">tiktok@...</p>
                  </button>
                </div>
              </div>

              {/* Préfixe personnalisé (si mode custom) */}
              {createMode === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Préfixe de l'alias
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={customPrefix}
                      onChange={(e) => setCustomPrefix(e.target.value.toLowerCase())}
                      className="input-field flex-1"
                      placeholder="tiktok"
                      data-testid="custom-prefix-input"
                    />
                    <span className="text-slate-600">@demo-essihault.com</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Si déjà pris, un numéro sera ajouté (ex: tiktok2)
                  </p>
                </div>
              )}

              {/* Type d'alias */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Type d'alias
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setAliasType('temporary')}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      aliasType === 'temporary'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="type-temporary-btn"
                  >
                    <Zap className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                    <p className="text-sm font-medium">Temporaire</p>
                    <p className="text-xs text-slate-500">Expire 1h</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAliasType('permanent')}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      aliasType === 'permanent'
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="type-permanent-btn"
                  >
                    <Shield className="w-5 h-5 mx-auto mb-1 text-indigo-500" />
                    <p className="text-sm font-medium">Permanent</p>
                    <p className="text-xs text-slate-500">Jamais supprimé</p>
                  </button>
                </div>
              </div>

              {createError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="create-alias-error">
                  {createError}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCustomPrefix('');
                    setCreateError('');
                  }}
                  className="btn btn-secondary flex-1"
                  data-testid="cancel-create-btn"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="btn btn-primary flex-1"
                  data-testid="confirm-create-btn"
                >
                  {creating ? 'Création...' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
